/**
 * VacationApprover — aprobación con contexto (art. 38 ET).
 *
 * Pendientes + calendario de equipo con cobertura por centro:
 * nadie aprueba agosto a ciegas.
 */
import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from './AdminLayout';
import {
  getPendingVacations,
  approveVacation,
  rejectVacation,
  getTeamCalendar,
  VacationRequestItem,
  TeamCalendarDay,
} from '../../services/apiClient';

const MAX_DIAS_POR_PETICION = 90;

function sumarDias(iso: string, dias: number): string {
  // Aritmética de calendario pura en UTC. Jamás `new Date(iso + 'T00:00:00')`
  // + `toISOString()`: esa medianoche es LOCAL y en cualquier huso por delante
  // de UTC (Europe/Madrid) cae en el día anterior al pasarla a UTC, así que
  // sumarDias(X, 1) devolvía X, el cursor de los tramos no avanzaba nunca y el
  // calendario se pedía 24 veces con el último día duplicado ~22 veces.
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + dias)).toISOString().slice(0, 10);
}

/** El calendario del equipo entre dos fechas, en tramos que el endpoint acepte. */
async function cargarCalendarioPorTramos(
  desde: string,
  hasta: string,
): Promise<TeamCalendarDay[]> {
  const dias: TeamCalendarDay[] = [];
  let cursor = desde;
  // Tope de vueltas: un rango absurdo no debe colgar la pantalla.
  for (let i = 0; i < 24 && cursor <= hasta; i++) {
    const fin = sumarDias(cursor, MAX_DIAS_POR_PETICION - 1);
    const tramoFin = fin > hasta ? hasta : fin;
    dias.push(...(await getTeamCalendar(cursor, tramoFin)));
    cursor = sumarDias(tramoFin, 1);
  }
  return dias;
}

export function VacationApprover() {
  const [pending, setPending] = useState<VacationRequestItem[]>([]);
  const [calendar, setCalendar] = useState<TeamCalendarDay[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const reqs = await getPendingVacations();
    setPending(reqs);
    if (reqs.length > 0) {
      // El endpoint corta a 92 días. Con dos solicitudes separadas —una en
      // enero y otra en agosto— el rango pedido se pasaba, el 422 caía en un
      // `catch` mudo y el calendario se quedaba VACÍO: el aviso de cobertura
      // desaparecía justo en la pantalla que existe para que nadie apruebe
      // agosto a ciegas. Se pide por tramos y se junta.
      const starts = reqs.map((r) => r.start_date).sort();
      const ends = reqs.map((r) => r.end_date).sort();
      try {
        setCalendar(await cargarCalendarioPorTramos(starts[0], ends[ends.length - 1]));
        setError(null);
      } catch {
        setCalendar([]);
        setError('No se pudo cargar la cobertura del equipo: aprueba con cuidado');
      }
    }
  }, []);

  useEffect(() => {
    load().catch(() => setError('Error cargando solicitudes'));
  }, [load]);

  const act = async (id: number, action: 'approve' | 'reject') => {
    setError(null);
    try {
      if (action === 'approve') await approveVacation(id);
      else await rejectVacation(id);
      await load();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setError(detail ?? 'Error');
    }
  };

  const coverageForRange = (r: VacationRequestItem) =>
    calendar
      .filter((d) => d.date >= r.start_date && d.date <= r.end_date)
      .flatMap((d) => d.worksites)
      .filter((w) => w.below_min_staff);

  return (
    <AdminLayout title="Vacaciones">
      <div className="p-4 space-y-3">
        {error && (
          <div className="bg-danger-soft text-danger rounded-lg p-3 text-sm">
            {error}
          </div>
        )}

        {pending.map((r) => {
          const coverageIssues = coverageForRange(r);
          return (
            <div key={r.id} className="bg-surface rounded-xl p-4 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-ink">
                    {r.user_name ?? `Usuario #${r.user_id}`} · {r.start_date} → {r.end_date}
                  </p>
                  <p className="text-sm text-ink-2">
                    {r.days_natural} días naturales · cargo a {r.charge_year}
                  </p>
                  {r.notes && (
                    <p className="text-sm text-ink-3 mt-1">"{r.notes}"</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => act(r.id, 'approve')}
                    className="px-3 py-2 bg-brand text-white rounded-lg text-sm hover:bg-brand-2"
                  >
                    Aprobar
                  </button>
                  <button
                    onClick={() => act(r.id, 'reject')}
                    className="px-3 py-2 bg-danger text-white rounded-lg text-sm"
                  >
                    Rechazar
                  </button>
                </div>
              </div>

              {/* Avisos de contexto */}
              <div className="mt-3 space-y-1">
                {/* r2b20[2]: el preaviso lo sella el BACKEND al aprobar, con
                    el plazo de la jurisdicción del trabajador (ES 2 meses,
                    AD ni siquiera lo fija). Aquí se pinta esa prueba tal cual:
                    `notice_ok` null es «aún sin sellar» (está pendiente) o
                    «jurisdicción sin plazo codificado», y esta pantalla no
                    calcula plazos ni cita leyes que pueden no ser las del
                    tenant. */}
                {r.notice_ok === true && (
                  <p className="text-xs text-brand">Preaviso registrado</p>
                )}
                {r.notice_ok === false && (
                  <p className="text-xs text-warn">Preaviso no acreditado</p>
                )}
                {coverageIssues.length > 0 && (
                  <p className="text-xs text-danger">
                    ⚠ Cobertura: {coverageIssues.length} día(s)-centro por debajo
                    del mínimo si se aprueba (
                    {[...new Set(coverageIssues.map((w) => w.worksite_name))].join(', ')}
                    )
                  </p>
                )}
              </div>
            </div>
          );
        })}

        {pending.length === 0 && (
          <p className="text-center text-sm text-ink-3 py-10">
            Sin solicitudes pendientes ✓
          </p>
        )}

        {/* Calendario compacto de ausencias */}
        {calendar.length > 0 && (
          <div className="bg-surface rounded-xl p-4 shadow-sm">
            <h3 className="font-medium text-ink mb-2 text-sm">
              Ausencias en el periodo
            </h3>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {calendar
                .filter((d) => d.absent.length > 0)
                .map((d) => (
                  <div key={d.date} className="flex text-xs gap-2">
                    <span className="text-ink-3 w-20 shrink-0">{d.date}</span>
                    <span className="text-ink-2">
                      {d.absent.map((a) => a.user_name).join(', ')}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
